import React, { useRef, useEffect } from 'react';

/**
 * TabNav — horizontal tab bar with bottom-border underline.
 *
 * Used by every tabbed page in the platform: Compliance Center (9
 * tabs), Policies (8 tabs), Admin Center (6 tabs), Workflow Audit
 * (3 source tabs), Slideout (4 detail tabs), etc.
 *
 * Selection is *controlled*. Keyboard navigation per the WAI-ARIA
 * Authoring Practices for tabs:
 *   - Tab moves focus into / out of the tablist.
 *   - ArrowLeft / ArrowRight cycle through tabs.
 *   - Home / End jump to first / last.
 *   - Activation is automatic (focus = activate) which matches Wiz/Linear.
 *
 * @param {Object} props
 * @param {Array<{
 *   id: string,
 *   label: React.ReactNode,
 *   badge?: React.ReactNode,
 *   dot?: 'success'|'warning'|'danger'|'action',
 *   disabled?: boolean,
 * }>} props.tabs
 * @param {string}                 props.activeId
 * @param {(id: string) => void}   props.onChange
 * @param {string}                 [props.ariaLabel='Tabs']
 * @param {string}                 [props.className]
 */
export function TabNav({ tabs, activeId, onChange, ariaLabel = 'Tabs', className = '' }) {
  const tablistRef = useRef(null);

  const enabledTabs = tabs.filter((t) => !t.disabled);
  const activeIndex = enabledTabs.findIndex((t) => t.id === activeId);

  const focusTab = (idx) => {
    const tab = tablistRef.current?.querySelectorAll('[role="tab"]:not([disabled])')[idx];
    if (tab) tab.focus();
  };

  const onKey = (e) => {
    if (!enabledTabs.length) return;
    let next = activeIndex;
    if (e.key === 'ArrowRight') next = (activeIndex + 1) % enabledTabs.length;
    else if (e.key === 'ArrowLeft') next = (activeIndex - 1 + enabledTabs.length) % enabledTabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = enabledTabs.length - 1;
    else return;
    e.preventDefault();
    onChange && onChange(enabledTabs[next].id);
    // Defer focus until after re-render so tabIndex flips
    window.setTimeout(() => focusTab(next), 0);
  };

  const DOT_COLOR = {
    success: 'bg-v2-success',
    warning: 'bg-v2-warning',
    danger:  'bg-v2-danger',
    action:  'bg-v2-action',
  };

  return (
    <div
      ref={tablistRef}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onKey}
      className={'flex items-center gap-0 border-b border-v2-border-subtle ' + className}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeId;
        return (
          <button
            key={tab.id}
            role="tab"
            type="button"
            disabled={tab.disabled}
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => !tab.disabled && onChange && onChange(tab.id)}
            className={
              'v2-focus-ring inline-flex items-center gap-2 ' +
              'px-4 py-2.5 text-[13px] font-medium ' +
              'border-b-2 -mb-px transition-colors duration-v2 ease-v2 ' +
              'disabled:opacity-50 disabled:cursor-not-allowed ' +
              (isActive
                ? 'border-v2-action text-v2-action font-semibold'
                : 'border-transparent text-v2-ink-muted hover:text-v2-ink')
            }
          >
            {tab.dot && (
              <span aria-hidden="true" className={'w-1.5 h-1.5 rounded-full ' + (DOT_COLOR[tab.dot] || 'bg-v2-action')} />
            )}
            <span>{tab.label}</span>
            {tab.badge && (
              <span aria-hidden="true">{tab.badge}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default TabNav;
